import { LoginOverlay } from "@/components/LoginOverlay";
import { API_BASE_URL } from "@/lib/constants";
import { lensburyFetch, ResponseError } from "@/lib/utils";
import { createContext, useContext, useRef, useState, type ReactNode } from "react";

export interface User {
  createdAt: Date,
  updatedAt: Date,
  username: string,
  firstName: string,
  lastName: string,
  lensburyUserId: number,
  lensburyMembershipId: number,
  lensburyMembershipType: string,
  lensburyMembershipStatus: string,
  forwardingId: string,
  lastActivity?: string,
}

interface AuthContextType {
  fetchWithAuth: (method: string, endpoint: string, body?: any) => Promise<any>,
  getUser: () => Promise<User>,
  updateUser: (updates: Partial<User>) => Promise<void>,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [onLogin, setOnLogin] = useState<() => void>();
  const user = useRef<User | undefined>(undefined);
  const loginPromise = useRef<Promise<void> | undefined>(undefined);

  const login = () => {
    if (!loginPromise.current) {
      loginPromise.current = new Promise<void>((res) => setOnLogin(() => res)).finally(() => {
        setOnLogin(undefined);
        loginPromise.current = undefined;
      });
    }
    return loginPromise.current;
  }

  const fetchWithAuth = async (method: string, endpoint: string, body?: any) => {
    try {
      return await lensburyFetch(method, endpoint, body);
    } catch (e) {
      if (e instanceof ResponseError && e.statusCode == 401) {
        await login();
        return await lensburyFetch(method, endpoint, body);
      }
      throw e;
    }
  };

  const getUser = async () => {
    if (!user.current) {
      user.current = await fetchWithAuth("GET", "/user");
    }
    return user.current!;
  };

  const updateUser = async (updates: Partial<User>) => {
    await fetchWithAuth("PATCH", "/user", updates);
    user.current = { ...user.current!, ...updates };
  };


  return (
    <AuthContext.Provider value={{ fetchWithAuth, getUser, updateUser }}>
      <LoginOverlay onLogin={onLogin} />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext)!;
}
